import { CloseOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Empty, Flex, Form, Input, Table } from "antd"
import type { ColumnType } from "antd/es/table"
import React from "react"

import InputNumber from "../../../../../../components/Input/InputNumber.tsx"
import { formListAddFocus } from "../../../../../../utils/form/form-list-add-focus.ts"
import { CustomTableBlockAddColumnPopover } from "./CustomTableBlockAddColumnPopover.tsx"

interface CustomTableBlockProps {
  fieldKey: string
}

export function CustomTableBlock({ fieldKey }: CustomTableBlockProps) {
  const form = Form.useFormInstance()

  const columnsPath = ["block_data", fieldKey, "columns"]
  const rowsPath = ["block_data", fieldKey, "rows"]

  const getColumns = (): { name: string; col_type: string }[] =>
    form.getFieldValue(columnsPath) ?? []

  const getRows = (): Record<string, any>[] =>
    form.getFieldValue(rowsPath) ?? []

  const isDuplicateName = (name: string) =>
    getColumns().some((c) => c.name === name)

  const handleRemoveColumn = (colIndex: number) => {
    const cols = getColumns()
    const removed = cols[colIndex]
    const next = cols.filter((_, i) => i !== colIndex)

    const fields: Parameters<typeof form.setFields>[0] = [
      { name: columnsPath, value: next, touched: true }
    ]

    if (removed?.name) {
      const rows = getRows().map((row) => {
        const { [removed.name]: _, ...rest } = row
        return rest
      })
      fields.push({ name: rowsPath, value: rows, touched: true })
    }

    form.setFields(fields)
  }

  return (
    <>
      <Form.Item name={["block_data", fieldKey, "title"]} label="Table Title">
        <Input placeholder="Enter table title" />
      </Form.Item>

      {/* hidden binders so antd tracks these lists */}
      <Form.Item name={columnsPath} hidden>
        <Input />
      </Form.Item>

      <Form.Item label="Table">
        <Form.Item noStyle shouldUpdate>
          {() => {
            const columns = getColumns()
            const validColumns = columns.filter((c) => c?.name?.trim())

            if (!validColumns.length) {
              return (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No columns defined"
                >
                  <CustomTableBlockAddColumnPopover
                    form={form}
                    getColumns={getColumns}
                    columnsPath={columnsPath}
                    getRows={getRows}
                    rowsPath={rowsPath}
                    validColumns={validColumns}
                    isDuplicateName={isDuplicateName}
                  />
                </Empty>
              )
            }

            return (
              <Form.List name={rowsPath}>
                {(fields, { add, remove }) => {
                  const tableColumns: ColumnType<any>[] = [
                    ...validColumns.map((col, colIdx) => ({
                      title: (
                        <Flex align="center" justify="space-between" gap={4}>
                          <span>{col.name}</span>
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => handleRemoveColumn(colIdx)}
                            style={{ marginLeft: 4 }}
                          />
                        </Flex>
                      ),
                      dataIndex: col.name,
                      render: (_: any, __: any, rowIndex: number) => (
                        <Form.Item
                          name={[rowIndex, col.name]}
                          rules={[
                            ...(col.col_type === "number"
                              ? []
                              : [
                                  {
                                    whitespace: true,
                                    message: "Column cannot be empty"
                                  }
                                ])
                          ]}
                        >
                          {col?.col_type === "number" ? (
                            <InputNumber
                              className={"w-full"}
                              placeholder={"Enter number"}
                              min={0}
                              max={999999999.99}
                              maxLength={20}
                              precision={2}
                              size="small"
                            />
                          ) : (
                            <Input.TextArea
                              autoSize
                              className={"w-full"}
                              placeholder={"Enter text"}
                              maxLength={500}
                              showCount
                              size={"small"}
                            />
                          )}
                        </Form.Item>
                      )
                    })),
                    {
                      title: (
                        <CustomTableBlockAddColumnPopover
                          iconOnly
                          form={form}
                          getColumns={getColumns}
                          columnsPath={columnsPath}
                          getRows={getRows}
                          rowsPath={rowsPath}
                          validColumns={validColumns}
                          isDuplicateName={isDuplicateName}
                        />
                      ),
                      width: 50,
                      render: (_: any, __: any, rowIndex: number) => (
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<CloseOutlined />}
                          onClick={() => remove(rowIndex)}
                        />
                      )
                    }
                  ]

                  return (
                    <Flex vertical gap={8}>
                      <Table
                        columns={tableColumns}
                        dataSource={fields.map((f) => ({ ...f, key: f.key }))}
                        pagination={false}
                        size="small"
                        locale={{ emptyText: "No rows yet" }}
                      />
                      <Button
                        type="dashed"
                        onClick={() => {
                          formListAddFocus(
                            () => add({}),
                            fields,
                            form,
                            rowsPath,
                            [validColumns[0]?.name]
                          )
                        }}
                        block
                        icon={<PlusOutlined />}
                      >
                        Add row
                      </Button>
                    </Flex>
                  )
                }}
              </Form.List>
            )
          }}
        </Form.Item>
      </Form.Item>
    </>
  )
}
